import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  getColumnWidths,
  saveColumnWidth,
  resetColumnWidths,
  getColumnWidthsCacheKey,
} from '../config/columnWidths';

const MIN_WIDTH = 50;
const MAX_WIDTH = 500;
const CHAR_WIDTH = 9;
const BASE_PADDING = 30;
const MIN_DEFAULT_WIDTH = 80;
const MAX_DEFAULT_WIDTH = 200;

function calculateDefaultWidth(columnName) {
  const calculatedWidth = columnName.length * CHAR_WIDTH + BASE_PADDING;
  return Math.max(MIN_DEFAULT_WIDTH, Math.min(MAX_DEFAULT_WIDTH, calculatedWidth));
}

export function useColumnWidths(url, searchParams, columns) {
  const cacheKey = useMemo(() => getColumnWidthsCacheKey(url, searchParams), [url, searchParams]);

  const getDefaultWidths = useCallback(() => {
    const defaults = {};
    columns.forEach(col => {
      defaults[col.dataField] = calculateDefaultWidth(col.name);
    });
    return defaults;
  }, [columns]);

  const [columnWidths, setColumnWidths] = useState(() => {
    const savedWidths = getColumnWidths(cacheKey);
    const defaultWidths = {};

    columns.forEach(col => {
      const defaultWidth = calculateDefaultWidth(col.name);
      defaultWidths[col.dataField] = savedWidths[col.dataField] || defaultWidth;
    });

    return defaultWidths;
  });

  const isResizing = useRef(false);
  const resizeColumn = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const currentWidth = useRef(0);
  const tableRef = useRef(null);

  useEffect(() => {
    const savedWidths = getColumnWidths(cacheKey);
    const newWidths = {};

    columns.forEach(col => {
      const defaultWidth = calculateDefaultWidth(col.name);
      newWidths[col.dataField] = savedWidths[col.dataField] || defaultWidth;
    });

    setColumnWidths(newWidths);
  }, [cacheKey, columns]);

  const updateColumnDOMWidth = useCallback((columnField, width) => {
    if (!tableRef.current) {
      tableRef.current = document.querySelector('.table-content table');
    }
    if (!tableRef.current) return;

    const cells = tableRef.current.querySelectorAll(`[data-column="${columnField}"]`);
    cells.forEach(cell => {
      cell.style.width = `${width}px`;
      cell.style.minWidth = `${width}px`;
      cell.style.maxWidth = `${width}px`;
    });
  }, []);

  const handleResizeStart = useCallback(
    (e, columnField) => {
      e.preventDefault();
      e.stopPropagation();

      isResizing.current = true;
      resizeColumn.current = columnField;
      startX.current = e.clientX;
      startWidth.current = columnWidths[columnField] || MIN_DEFAULT_WIDTH;
      currentWidth.current = startWidth.current;

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [columnWidths]
  );

  const handleResizeMove = useCallback(
    e => {
      if (!isResizing.current || !resizeColumn.current) return;

      const diff = e.clientX - startX.current;
      const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth.current + diff));
      currentWidth.current = newWidth;

      updateColumnDOMWidth(resizeColumn.current, newWidth);
    },
    [updateColumnDOMWidth]
  );

  const handleResizeEnd = useCallback(() => {
    if (isResizing.current && resizeColumn.current) {
      const columnField = resizeColumn.current;
      const finalWidth = currentWidth.current;

      setColumnWidths(prev => ({
        ...prev,
        [columnField]: finalWidth,
      }));

      saveColumnWidth(cacheKey, columnField, finalWidth);
    }

    isResizing.current = false;
    resizeColumn.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [cacheKey]);

  useEffect(() => {
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);

  const resetWidths = useCallback(() => {
    resetColumnWidths(cacheKey);
    const newWidths = getDefaultWidths();
    setColumnWidths(newWidths);
    tableRef.current = null;
  }, [cacheKey, getDefaultWidths]);

  const getColumnStyle = useCallback(
    columnField => {
      const width = columnWidths[columnField] || MIN_DEFAULT_WIDTH;
      return {
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
      };
    },
    [columnWidths]
  );

  return {
    columnWidths,
    handleResizeStart,
    resetWidths,
    getColumnStyle,
  };
}
